import React, { useState, useEffect } from 'react';
import { IconButton, Tooltip, Badge } from '@mui/material';
import { 
  Favorite as FavoriteIcon, 
  FavoriteBorder as FavoriteBorderIcon 
} from '@mui/icons-material';
import { useLikes } from '../../hooks/useLikes';

const LikeButton = ({ 
  contentType, 
  objectId, 
  size = 'medium',
  showCount = false,
  initialLikeCount = 0,
  sx = {},
  onLikeChange = null,
  disabled = false
}) => {
  const { isLiked, toggleLike, isToggling, isAuthenticated } = useLikes();
  const [likeCount, setLikeCount] = useState(initialLikeCount);
  const [isLikedState, setIsLikedState] = useState(false);
  
  // Update local state when hook data changes
  useEffect(() => {
    if (isAuthenticated) {
      const liked = isLiked(contentType, objectId);
      setIsLikedState(liked);
    }
  }, [isLiked, contentType, objectId, isAuthenticated]);
  
  const handleLikeToggle = async () => {
    if (disabled || isToggling || !isAuthenticated) return;
    
    try {
      const success = await toggleLike(contentType, objectId);
      if (success) {
        const newLikedState = !isLikedState;
        setIsLikedState(newLikedState);
        
        // Update like count
        if (newLikedState) {
          setLikeCount(prev => prev + 1);
        } else {
          setLikeCount(prev => Math.max(0, prev - 1));
        }
        
        // Call callback if provided
        if (onLikeChange) {
          onLikeChange(newLikedState, likeCount);
        }
      }
    } catch (error) {
      console.error('Failed to toggle like:', error);
    }
  };
  
  const iconSize = size === 'small' ? 20 : size === 'large' ? 28 : 24;
  
  // Show different tooltip based on authentication and like state
  const getTooltipText = () => {
    if (!isAuthenticated) return 'Sign in to like content';
    return isLikedState ? 'Unlike' : 'Like';
  };
  
  return (
    <Tooltip title={getTooltipText()}>
      <span>
        <IconButton
          onClick={handleLikeToggle}
          disabled={disabled || isToggling || !isAuthenticated}
          sx={{
            color: isLikedState ? 'error.main' : 'text.secondary',
            minWidth: '3.5rem',
            minHeight: '2.5rem',
            borderRadius: '8px',
            '&:hover': {
              color: isLikedState ? 'error.dark' : 'error.main',
              transform: 'scale(1.05)',
              transition: 'all 0.2s ease-in-out',
              backgroundColor: 'rgba(255, 255, 255, 0.04)',
            },
            '&:disabled': {
              opacity: 0.5,
            },
            ...sx
          }}
          size={size}
        >
          {showCount ? (
            <Badge badgeContent={likeCount} color="error">
              {isLikedState ? (
                <FavoriteIcon sx={{ fontSize: iconSize }} />
              ) : (
                <FavoriteBorderIcon sx={{ fontSize: iconSize }} />
              )}
            </Badge>
          ) : (
            isLikedState ? (
              <FavoriteIcon sx={{ fontSize: iconSize }} />
            ) : (
              <FavoriteBorderIcon sx={{ fontSize: iconSize }} />
            )
          )}
        </IconButton>
      </span>
    </Tooltip>
  );
};

export default LikeButton;
